// src/stores/AuthStore.js (Pinia)
import { defineStore } from "pinia"
import axios from "axios"
import { useOrganizationStore } from "./organization_store"

// For Heroku: https://medium.com/@fishpercolator/deploying-your-nuxt-rails-api-app-to-production-with-heroku-54efd09eec22
//
// From https://medium.com/@charliepython/pinia-firebase-v9-and-axios-with-vue-3-composition-6261626da931
// const axiosPrivate = axios.create({
//   baseURL: import.meta.env.VITE_ROTULU_API_import.meta.env.VITE_API_BASE,
//   withCredentials: true,
// })
export const PREFIX = 'iP4BLqnibqamRTI_'

export const useAuthStore = defineStore("AuthStore", {
  id: 'auth',
  state: () => ({
    auth_token: null,
    refresh_token: null,
    user: {
      id: null,
      first_name: null,
      middle_initial: null,
      last_name: null,
      role: null,
      version: null,
      simulated_role: null,
      username: null,
      email: null,
      google_secret: null,
      organization_id: null,
      organization_slug: null,
      organization_name: null,
      organization_role: null,
      force_password_change: false
    },
    is_refreshing_token: false,
    allow_mfa_setup: false,
    login_status: null,
    login_message: null
  }),
  getters: {
    getAuthToken: (state) => {
      return state.auth_token || localStorage.getItem(`${PREFIX}auth_token`)
    },
    getAuthTokenHeader: (state) => {
      let token = state.auth_token || localStorage.getItem(`${PREFIX}auth_token`)
      if (token && token.indexOf('Bearer') !== 0) {
        token = `Bearer ${token}`
      }
      return {
        headers: {
          authorization: token
        },
      }
    },
    getRefreshToken: (state) => {
      return state.refresh_token || localStorage.getItem(`${PREFIX}refresh_token`)
    },
    getRefreshTokenParam: (state) => {
      return { refresh_token: state.refresh_token || localStorage.getItem(`${PREFIX}refresh_token`) }
    },
    getUserInitials: (state) => {
      let first, middle, last
      if (state.user?.first_name) {
        first = state.user?.first_name?.substring(0, 1) || ''
        middle = state.user?.middle_initial?.substring(0, 1) || ''
        last = state.user?.last_name?.substring(0, 1) || ''
      } else if (localStorage.getItem(`${PREFIX}auth_token_user`)) {
        // auth_token_user: user id, first name, middle initial, last name, role, simulated role
        const userInfo = localStorage.getItem(`${PREFIX}auth_token_user`).split('|')
        first = userInfo[1]?.substring(0, 1) || ''
        middle = userInfo[2]?.substring(0, 1) || ''
        last = userInfo[3]?.substring(0, 1) || ''
      }
      return first + middle + last
    },
    getUserID(state) {
      if (state.user?.id) {
        return state.user?.id
      } else if (localStorage.getItem(`${PREFIX}auth_token_user`)) {
        // auth_token_user: user id, first name, middle initial, last name, role, simulated role
        const userInfo = localStorage.getItem(`${PREFIX}auth_token_user`).split('|')
        return userInfo[0]
      }
      return null
    },
    getUserEmail(state) {
      return state.user?.email
    },
    getUserOrganizationId(state) {
      return state.user?.organization_id
    },
    getUserOrganizationRole(state) {
      return state.user?.organization_role
    },
    isLoggedIn(state) {
      const auth_token = state.getAuthToken
      const loggedOut = !auth_token || auth_token === 'null' || auth_token === 'undefined'
      return !loggedOut
    },
    allowMFASetup(state) {
      return state.allow_mfa_setup
    },
    getGoogleSecret(state) {
      return state.user?.google_secret
    },
    isVersionCurrent(state) {
      console.log(`${process.env.VUE_APP_ENV_VERSION} vs. ${state.user?.version}`)
      // Needed for development purposes
      if (!process.env.VUE_APP_ENV_VERSION || !state.user?.version) { return true }

      return process.env.VUE_APP_ENV_VERSION === state.user?.version
    },
    getSimulatedRole(state) {
      // This function can only run with proper permissions to simulate a role
      if (!state.isFinanceAdminOrAdminSkipSimulation) {
        return null
      }

      // Check by role
      if (state.user?.simulated_role) {
        return state.user.simulated_role
      } else if (localStorage.getItem(`${PREFIX}auth_token_user`)) {
        // auth_token_user: user id, first name, middle initial, last name, role, simulated role
        const userInfo = localStorage.getItem(`${PREFIX}auth_token_user`).split('|')
        if (userInfo[5]) { return userInfo[5] }
      }

      return null
    },
    isPermitted(state) {
      // This is the only way to allow a parameter to be passed in.
      // See https://pinia.vuejs.org/core-concepts/getters.html
      return (allowedRoles, is_skip_simulation = false) => {
        // Ensure we are logged in
        if (!state.isLoggedIn) { return false }

        // Return false if simulated role exists and does not match
        // (simulated_role is never enough to return "true". The real role also needs to allow this.)
        if (!is_skip_simulation && state.user?.simulated_role) {
          if (!allowedRoles.includes(state.user.simulated_role)) { return false }
        } else if (!is_skip_simulation && localStorage.getItem(`${PREFIX}auth_token_user`)) {
          // auth_token_user: user id, first name, middle initial, last name, role, simulated role
          const userInfo = localStorage.getItem(`${PREFIX}auth_token_user`).split('|')
          if (userInfo[5] && !allowedRoles.includes(userInfo[5])) { return false }
        }

        // Check by role
        if (state.user?.role) {
          if (allowedRoles.includes(state.user.role)) { return true }
        } else if (localStorage.getItem(`${PREFIX}auth_token_user`)) {
          // auth_token_user: user id, first name, middle initial, last name, role, simulated role
          const userInfo = localStorage.getItem(`${PREFIX}auth_token_user`).split('|')
          if (allowedRoles.includes(userInfo[4])) { return true }
        }

        return false
      }
    },
    isAdmin(state) {
      const allowedRoles = ['DeveloperAdmin-311', 'SuperAdmin-1450', 'Admin']
      return state.isPermitted(allowedRoles)
    },
    isDeveloperAdmin(state) {
      const allowedRoles = ['DeveloperAdmin-311']
      return state.isPermitted(allowedRoles)
    },
    // Need this for simulating users on and off
    isFinanceAdminOrAdminSkipSimulation(state) {
      const allowedRoles = ['DeveloperAdmin-311', 'SuperAdmin-1450', 'Admin', 'FinanceAdmin']
      return state.isPermitted(allowedRoles, true)
    },
    // Need this for simulating users on and off
    isSuperAdminOrFinanceAdminSkipSimulation(state) {
      const allowedRoles = ['DeveloperAdmin-311', 'SuperAdmin-1450', 'FinanceAdmin']
      return state.isPermitted(allowedRoles, true)
    },
    // Need this for simulating users on and off
    isSuperAdminSkipSimulation(state) {
      const allowedRoles = ['DeveloperAdmin-311', 'SuperAdmin-1450']
      return state.isPermitted(allowedRoles, true)
    },
    // Need this for simulating users on and off
    isSuperAdminOrFinanceAdmin(state) {
      const allowedRoles = ['DeveloperAdmin-311', 'SuperAdmin-1450', 'FinanceAdmin']
      return state.isPermitted(allowedRoles)
    },
    // This approach is hackable by another user role, but that is not the concern. It 
    isFinanceOrSuperAdmin(state) {
      const allowedRoles = ['DeveloperAdmin-311', 'SuperAdmin-1450', 'Finance', 'FinanceAdmin']
      return state.isPermitted(allowedRoles)
    },
    // This approach is hackable by another user role, but that is not the concern. It
    isFinanceAdminOrAdmin(state) {
      const allowedRoles = ['DeveloperAdmin-311', 'SuperAdmin-1450', 'Admin', 'FinanceAdmin']
      return state.isPermitted(allowedRoles)
    },
    // This is whether we should show easy links to payments info. It is still accessible from the URL for others.
    isFinanceOrAdmin(state) {
      const allowedRoles = ['SuperAdmin-1450', 'DeveloperAdmin-311', 'Admin', 'Finance', 'FinanceAdmin']
      return state.isPermitted(allowedRoles)
    },
    isFinanceAdminOrSales(state) {
      // Shipping is the lowest permissions, so fetch via opposite logic, but do ensure we are logged in!
      const allowedRoles = ['Sales', 'SuperAdmin-1450', 'DeveloperAdmin-311', 'Admin', 'Finance', 'FinanceAdmin']
      return state.isLoggedIn && state.isPermitted(allowedRoles)
    },
    isShipping(state) {
      // Shipping is the lowest permissions, so fetch via opposite logic, but do ensure we are logged in!
      const allowedRoles = ['Sales', 'SuperAdmin-1450', 'DeveloperAdmin-311', 'Admin', 'Finance', 'FinanceAdmin']
      return state.isLoggedIn && !state.isPermitted(allowedRoles)
    },
    // This is whether we should show easy links to payments info. It is still accessible from the URL for others.
    canEmailOrderConfirmation(state) {
      const allowedRoles = ['SuperAdmin-1450', 'DeveloperAdmin-311', 'Admin', 'Sales', 'Finance', 'FinanceAdmin']
      return state.isPermitted(allowedRoles)
    },
    // This is not super secure in that someone could hack to see this information if they are already logged in.
    canModifyPayments(state) {
      // Check by role
      const allowedRoles = ['DeveloperAdmin-311', 'SuperAdmin-1450', 'FinanceAdmin']
      return state.isPermitted(allowedRoles)
    },
    // Check if user can access admin panel (for hiding/showing Admin link)
    canAccessAdmin(state) {
      const allowedRoles = ['DeveloperAdmin-311', 'SuperAdmin-1450', 'Admin', 'FinanceAdmin']
      return state.isPermitted(allowedRoles)
    },
    getRole(state) {
      return state.user?.role || localStorage.getItem(`${PREFIX}auth_token_user`)?.split('|')[4]
    }
  },
  actions: {
    async loginUser(payload) {
      try {
        // 🔒 HTTPS FIX: Use same protocol as current page to avoid mixed content errors
        let rootBase
        if (process.env.VUE_APP_ENV_ROOT_BASE) {
          rootBase = process.env.VUE_APP_ENV_ROOT_BASE.replace('/api/v1/', '/')
        } else if (typeof window !== 'undefined') {
          // Detect if we're on HTTPS domain or HTTP IP
          const protocol = window.location.protocol // 'http:' or 'https:'
          const hostname = window.location.hostname
          
          console.log('🔍 DEBUG: protocol =', protocol, ', hostname =', hostname)
          
          // If accessed via HTTPS, always use relative URL (Nginx will proxy)
          if (protocol === 'https:') {
            rootBase = '/'
          } else if (hostname === 'localhost') {
            rootBase = 'http://localhost:3000/'
          } else if (hostname === 'app.developmentdataanalytics.com') {
            // Domain name - use relative URL (Nginx will handle)
            rootBase = '/'
          } else {
            // IP address access - direct to backend port
            rootBase = `${protocol}//${hostname}:3000/`
          }
        } else {
          rootBase = 'http://localhost:3000/'
        }
        
        console.log('🔒 Login API endpoint:', `${rootBase}users/sign_in`)
        const response = await axios.post(`${rootBase}users/sign_in`, payload)
        this.setUserInfo(response)
        
        // If login was successful and we have an organization ID, set up organization from login response
        if (this.isLoggedIn && this.user.organization_id) {
          const organizationStore = useOrganizationStore();
          
          // Create organization object from login response data
          const currentOrg = {
            id: this.user.organization_id,
            slug: this.user.organization_slug,
            name: this.user.organization_name,
            is_admin: true // Assume admin for now
          };
          
          // Set the current organization directly from login data
          organizationStore.setCurrentOrganization(currentOrg);
          organizationStore.organizations = [currentOrg];
        }
      } catch (error) {
        this.is_refreshing_token = false
        this.reject(error)
      }
    },
    async logoutUser() {
      const config = {
        params: {
          refresh_token: this.refresh_token || localStorage.getItem(`${PREFIX}refresh_token`)
        }
      }
      // Do not want to authorize
      delete axios.defaults.headers.common["Authorization"]
      
      // Clear organization data
      try {
        const organizationStore = useOrganizationStore();
        organizationStore.clearOrganizationData();
      } catch (error) {
        console.error("Error clearing organization data:", error);
      }
      
      this.resetUserInfo()
      
      try {
        // logging out errors out a lot, so ensure we have deleted tokens
        // 🔒 HTTPS FIX: Use same protocol as current page
        let baseUrl
        if (process.env.VUE_APP_ENV_ROOT_BASE) {
          baseUrl = process.env.VUE_APP_ENV_ROOT_BASE.replace('/api/v1/', '/')
        } else if (typeof window !== 'undefined') {
          const protocol = window.location.protocol
          const hostname = window.location.hostname
          
          // If accessed via HTTPS, always use domain-based URL (Nginx proxy)
          if (protocol === 'https:') {
            baseUrl = `${protocol}//${hostname}/`
          } else if (hostname === 'localhost') {
            baseUrl = 'http://localhost:3000/'
          } else if (hostname === 'app.developmentdataanalytics.com') {
            baseUrl = `https://${hostname}/`
          } else {
            baseUrl = `${protocol}//${hostname}:3000/`
          }
        } else {
          baseUrl = 'https://159-203-115-94.nip.io/'
        }
        
        await axios.delete(`${baseUrl}users/sign_out`, config)
            .catch((error) => {
              this.resetUserInfo()
              return Promise.reject(error)
            })
      } catch (error) {
        this.resetUserInfo()
        return Promise.reject(error)
      }
    },
    async refreshToken() {
      if (this.is_refreshing_token) {
        return
      }
      this.is_refreshing_token = true
      try {
        // 🔒 HTTPS FIX: Use same protocol as current page
        let rootBase
        if (process.env.VUE_APP_ENV_ROOT_BASE) {
          rootBase = process.env.VUE_APP_ENV_ROOT_BASE
        } else if (typeof window !== 'undefined') {
          const protocol = window.location.protocol
          const hostname = window.location.hostname
          
          // If accessed via HTTPS, always use domain-based URL (Nginx proxy)
          if (protocol === 'https:') {
            rootBase = `${protocol}//${hostname}/api/v1/`
          } else if (hostname === 'localhost') {
            rootBase = 'http://localhost:3000/api/v1/'
          } else if (hostname === 'app.developmentdataanalytics.com') {
            rootBase = `https://${hostname}/api/v1/`
          } else {
            rootBase = `${protocol}//${hostname}:3000/api/v1/`
          }
        } else {
          rootBase = 'http://localhost:3000/api/v1/'
        }
        
        const response = await axios.post(`${rootBase}tokens/refresh`, this.getRefreshTokenParam)
            .catch((error) => {
              this.is_refreshing_token = false
              this.resetUserInfo()
              return Promise.reject(error)
            })
        this.is_refreshing_token = false
        this.auth_token = response.data.auth_token
        this.refresh_token = response.data.refresh_token
        localStorage.setItem(`${PREFIX}auth_token`, response.data.auth_token)
        localStorage.setItem(`${PREFIX}refresh_token`, response.data.refresh_token)
      } catch (error) {
        this.is_refreshing_token = false
        this.resetUserInfo()
        return Promise.reject(error)
      }
    },
    setUserInfo(response) {
      this.auth_token = response.data.access_token || response.data.auth_token
      this.refresh_token = response.data.refresh_token
      
      // Map response fields to user object
      this.user = {
        id: response.data.id,
        first_name: response.data.first_name,
        middle_initial: response.data.middle_initial,
        last_name: response.data.last_name,
        role: response.data.role,
        version: response.data.version,
        simulated_role: response.data.simulated_role || null,
        username: response.data.username,
        email: response.data.email,
        google_secret: response.data.google_secret,
        organization_id: response.data.organization_id,
        organization_slug: response.data.organization_slug,
        organization_name: response.data.organization_name,
        organization_role: response.data.organization_role || 'user',
        force_password_change: response.data.force_password_change || false
      }
      
      localStorage.setItem(`${PREFIX}auth_token`, this.auth_token)
      localStorage.setItem(`${PREFIX}refresh_token`, this.refresh_token)
      localStorage.setItem(`${PREFIX}auth_token_user`, `${this.user.id}|${this.user.first_name}|${this.user.middle_initial}|${this.user.last_name}|${this.user.role}|${this.user.simulated_role}`)
      
      // Store organization info in localStorage
      if (this.user.organization_id) {
        localStorage.setItem(`${PREFIX}organization_id`, this.user.organization_id)
        localStorage.setItem(`${PREFIX}organization_slug`, this.user.organization_slug || '')
        localStorage.setItem(`${PREFIX}organization_name`, this.user.organization_name || '')
      }
    },
    resetUserInfo() {
      this.auth_token = null
      this.refresh_token = null
      this.user = {
        id: null,
        first_name: null,
        middle_initial: null,
        last_name: null,
        role: null,
        version: null,
        simulated_role: null,
        username: null,
        email: null,
        google_secret: null,
        organization_id: null,
        organization_slug: null,
        organization_name: null,
        organization_role: null,
      force_password_change: false
      }
      localStorage.removeItem(`${PREFIX}auth_token`)
      localStorage.removeItem(`${PREFIX}refresh_token`)
      localStorage.removeItem(`${PREFIX}auth_token_user`)
      localStorage.removeItem(`${PREFIX}organization_id`)
      localStorage.removeItem(`${PREFIX}organization_slug`)
      localStorage.removeItem(`${PREFIX}organization_name`)
    },
    reject(error) {
      if (error.response && error.response.status === 401) {
        this.resetUserInfo()
      }
      return Promise.reject(error)
    },
    simulateRole(role) {
      if (this.isFinanceAdminOrAdminSkipSimulation) {
        this.user.simulated_role = role
        localStorage.setItem(`${PREFIX}auth_token_user`, `${this.user.id}|${this.user.first_name}|${this.user.middle_initial}|${this.user.last_name}|${this.user.role}|${this.user.simulated_role}`)
      }
    }
  }
})